"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileApiClient = void 0;
const tslib_1 = require("tslib");
const axios_1 = tslib_1.__importDefault(require("axios"));
const form_data_1 = tslib_1.__importDefault(require("form-data"));
const models_1 = require("../models");
const utils_1 = require("../utils");
const internalFileEndpoint = "/internal/v1";
const externalFileEndpoint = "/external/v1";
class FileApiClient {
    constructor(opts = {}) {
        var _a, _b, _c, _d, _e;
        this.isInternal = (_a = opts.isInternal) !== null && _a !== void 0 ? _a : false;
        this.httpClient =
            (_b = opts.httpClient) !== null && _b !== void 0 ? _b : axios_1.default.create({
                baseURL: (_c = opts.fileApiBaseUrl) !== null && _c !== void 0 ? _c : process.env.FILE_API_BASE_URL,
                timeout: 15000,
            });
        this.filesCdnBaseUrl = (_e = (_d = opts.filesCdnBaseUrl) !== null && _d !== void 0 ? _d : process.env.FILES_CDN_BASE_URL) !== null && _e !== void 0 ? _e : "";
    }
    async uploadFile(file, isInternal = this.isInternal) {
        let contentLength = 0;
        if ("byteLength" in file.data) {
            contentLength = file.data.byteLength;
        }
        if ("size" in file.data) {
            contentLength = file.data.size;
        }
        const params = {
            fileType: file.type,
            fileExtension: file.extension,
            contentType: file.contentType,
            contentLength,
            metadata: file.metadata,
        };
        const fileUri = await (isInternal
            ? this.createPreSignedTrustedUploadUri(params)
            : this.createPreSignedUntrustedUploadUri(params));
        const base = {
            method: fileUri.uploadMethod,
            url: fileUri.url,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
        };
        if (!fileUri.fields) {
            await this.httpClient.request({
                ...base,
                data: file.data,
                headers: {
                    "Content-Type": fileUri.contentType,
                    "Content-Length": contentLength.toString(),
                },
            });
        }
        else {
            const formData = new form_data_1.default();
            Object.values(fileUri.fields).forEach(([key, value]) => {
                formData.append(key, value);
            });
            formData.append("file", file.data);
            await this.httpClient.request({
                ...base,
                data: formData,
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
        }
        return fileUri.id;
    }
    async downloadFile(fileId, isInternal = this.isInternal) {
        const createPresignedUrl = isInternal
            ? this.createPreSignedTrustedDownloadUrl.bind(this)
            : this.createPreSignedUntrustedDownloadUrl.bind(this);
        const fileUri = await createPresignedUrl(fileId);
        const response = await this.httpClient.request({
            method: "GET",
            url: fileUri.url,
            responseType: "arraybuffer",
        });
        const { fileType, fileExtension } = (0, utils_1.parseFileId)(fileId);
        const result = {
            data: Buffer.from(response.data, "binary"),
            type: fileType,
            extension: fileExtension,
        };
        return result;
    }
    async uploadFileStream({ stream, type, extension, trusted = false, metadata = {}, }) {
        var _a, _b;
        const contentType = Array.isArray(stream.headers["content-type"])
            ? stream.headers["content-type"][0]
            : stream.headers["content-type"];
        const headerContentLength = Array.isArray(stream.headers["content-length"])
            ? stream.headers["content-length"][0]
            : stream.headers["content-length"];
        if (!contentType) {
            throw new Error("Unable to determine remote file content type");
        }
        validateContentType(contentType);
        if (!headerContentLength) {
            throw new Error("Unable to determine remote file content length");
        }
        const contentLength = parseInt(headerContentLength, 10);
        if (isNaN(contentLength) || contentLength === 0) {
            throw new Error("Unable to determine content length or reported content length is 0");
        }
        const fileExtension = extension !== null && extension !== void 0 ? extension : determineExtension((_a = stream.config) === null || _a === void 0 ? void 0 : _a.url, contentType);
        const params = {
            fileType: type,
            fileExtension,
            contentType,
            contentLength,
            metadata: { ...metadata, originalUrl: (_b = stream.config) === null || _b === void 0 ? void 0 : _b.url },
        };
        const fileUri = await (trusted
            ? this.createPreSignedTrustedUploadUri(params)
            : this.createPreSignedUntrustedUploadUri(params));
        await this.httpClient.request({
            method: fileUri.uploadMethod,
            url: fileUri.url,
            data: stream.data,
            headers: {
                "Content-Type": fileUri.contentType,
                "Content-Length": contentLength.toString(),
            },
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
        });
        return {
            id: fileUri.id,
            url: this.getExternalUrl(fileUri.id),
            extension: fileExtension,
            contentType: contentType,
        };
    }
    getExternalUrl(fileId) {
        if (!this.filesCdnBaseUrl) {
            throw Error("Files CDN base url not provided and FILES_CDN_BASE_URL env variable unset");
        }
        return `${this.filesCdnBaseUrl}/api/file${externalFileEndpoint}/${fileId}/redirect`;
    }
    async createPreSignedTrustedUploadUri(data) {
        if (!this.isInternal) {
            throw new Error("Creating trusted pre-signed URIs can only be done internally");
        }
        const response = await this.httpClient.request({
            data,
            method: "POST",
            url: `${internalFileEndpoint}/upload-uri`,
        });
        return response.data;
    }
    async createPreSignedUntrustedUploadUri(data) {
        const response = await this.httpClient.request({
            data,
            method: "POST",
            url: `${externalFileEndpoint}/upload-uri`,
        });
        return response.data;
    }
    async createPreSignedTrustedDownloadUrl(fileId) {
        const response = await this.httpClient.request({
            method: "GET",
            url: `${internalFileEndpoint}/${fileId}`,
        });
        return response.data;
    }
    async createPreSignedUntrustedDownloadUrl(fileId) {
        const response = await this.httpClient.request({
            method: "GET",
            url: `${externalFileEndpoint}/${fileId}`,
        });
        return response.data;
    }
    async getMetadata(fileId) {
        const response = await this.httpClient.request({
            method: "GET",
            url: `${internalFileEndpoint}/${fileId}/metadata`,
        });
        return response.data;
    }
    async updateMetadata(fileId, metadata) {
        const data = metadata;
        await this.httpClient.request({
            method: "PUT",
            url: `${internalFileEndpoint}/${fileId}/metadata`,
            data,
        });
    }
    async zipFiles(files) {
        const r = await this.httpClient.request({
            data: files,
            method: "POST",
            url: `${internalFileEndpoint}/zip/files`,
        });
        return r.data;
    }
}
exports.FileApiClient = FileApiClient;
function extensionFromContentType(contentType) {
    switch (contentType) {
        case "text/csv":
            return models_1.FileExtension.CSV;
        case "image/png":
            return models_1.FileExtension.PNG;
        case "image/jpeg":
            return models_1.FileExtension.JPEG;
        case "image/gif":
            return models_1.FileExtension.GIF;
        case "image/tiff":
            return models_1.FileExtension.TIFF;
        case "application/pdf":
            return models_1.FileExtension.PDF;
        case "application/zip":
            return models_1.FileExtension.ZIP;
        case "application/json":
            return models_1.FileExtension.JSON;
        default:
            return null;
    }
}
function extensionFromURL(url) {
    if (!url) {
        return null;
    }
    try {
        const urlObject = new URL(url);
        const pathnameParts = urlObject.pathname.split(".");
        if (pathnameParts.length === 1) {
            return null;
        }
        return pathnameParts.pop();
    }
    catch (err) {
        return null;
    }
}
function determineExtension(url, contentType) {
    var _a;
    const extension = (_a = extensionFromURL(url)) !== null && _a !== void 0 ? _a : extensionFromContentType(contentType);
    if (!extension) {
        throw new Error("Unable to determine remote file extension");
    }
    return extension;
}
function validateContentType(contentType) {
    const values = [
        models_1.ContentType.ZIP,
        models_1.ContentType.CSV,
        models_1.ContentType.GIF,
        models_1.ContentType.PNG,
        models_1.ContentType.JPEG,
        models_1.ContentType.TIFF,
        models_1.ContentType.PDF,
        models_1.ContentType.XLSX,
        models_1.ContentType.JSON,
    ];
    if (values.includes(contentType)) {
        return;
    }
    throw new Error(`Invalid content type: ${contentType}`);
}
//# sourceMappingURL=FileApiClient.js.map